import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { Box, Container, Grid, Stack, Typography } from '@mui/material';
import LogoFacebook from '../../LogoFacebook';
import LogoInstagram from '../../LogoInstagram';
import Logo from '../../Logo';
import LogoWhatsApp from '../../LogoWhatsApp';
import LogoLinkedIn from '../../LogoLinkedIn';
import { HashLink } from 'react-router-hash-link';
import { useIsMacOs } from '../../../hooks/useIsMacOs';
import { appVersion } from 'config';

export interface LinkFooterProps {
  text: string;
  to: string;
  hashLink: boolean;
}

export interface LinkSectionProps {
  title: string;
  links: LinkFooterProps[];
}

export interface LandingFooterProps {
  address: string;
  copy: string;
  contact: {
    title: string;
    phone: string;
    email: string;
  };
  sections: LinkSectionProps[];
}

const RootStyle = styled(motion.div)(() => ({
  position: 'relative',
  width: '100%',
  background: '#F4F6F8',
  height: '100%',
  padding: '4em 0 0 0 '
}));

const Hr = styled(motion.hr)(({ theme }) => ({
  borderBottom: `2px solid ${theme.palette.grey}`
}));

const Enlace = styled(motion.a)(({ theme }) => ({
  textDecoration: 'none',
  color: theme.palette.grey['600']
}));
const HashEnlace = styled(HashLink)(({ theme }) => ({
  textDecoration: 'none',
  color: theme.palette.grey['600']
}));

export default function LandingFooter({ address, copy, contact, sections }: LandingFooterProps) {
  let [isMac] = useIsMacOs();

  return (
    <RootStyle>
      <Container maxWidth={'lg'}>
        <Grid container spacing={5}>
          <Grid item lg={5} sm={12}>
            <Stack direction={'column'} spacing={5}>
              <Stack direction={'row'} spacing={2}>
                <Logo />
                <LogoWhatsApp sx={{ padding: '.5em 0 0 0' }} />
                <LogoFacebook sx={{ padding: '.5em 0 0 0' }} />
                <LogoInstagram sx={{ padding: '.5em 0 0 0' }} />
                <LogoLinkedIn sx={{ padding: '.5em 0 0 0' }} />
              </Stack>
              <Typography sx={{ padding: isMac ? '20px 0' : '' }} variant={'body2'}>
                {address}
              </Typography>
              <Hr />
              <Stack direction={'column'} spacing={6}>
                <Typography variant={'overline'}>{contact.title}</Typography>
                <Stack direction={{ xs: 'column', sm: 'column', md: 'row' }} spacing={5}>
                  <Typography variant={'body2'}>{contact.phone}</Typography>
                  <Typography variant={'body2'}>{contact.email}</Typography>
                </Stack>
              </Stack>
            </Stack>
          </Grid>
          <Grid item lg={7} sm={12}>
            <Grid container>
              {sections.map((section, sectionIndex) => (
                <Grid key={sectionIndex} sm={12} xs={12} lg={4}>
                  <Stack spacing={5}>
                    <Box sx={{ margin: '.5em 0' }} />
                    <Typography variant={'overline'}>{section.title} </Typography>
                    <Stack spacing={4}>
                      {section.links.map((link, linkIndex) =>
                        link.hashLink ? (
                          <HashEnlace smooth key={linkIndex} to={link.to}>
                            <Typography variant={'body2'}>{link.text}</Typography>
                          </HashEnlace>
                        ) : (
                          <Enlace key={linkIndex} href={link.to}>
                            <Typography variant={'body2'}>{link.text}</Typography>
                          </Enlace>
                        )
                      )}
                    </Stack>
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Grid item lg={12} sm={12}>
            <Box sx={{ margin: { sm: '7em 0', xs: '7em 0' } }} />
            <Typography variant={'body2'}>{copy}</Typography>
            <Typography variant="body2">{appVersion}</Typography>
            <Box
              sx={{
                margin: { lg: '1.5em 0', xl: '1.5em 0', md: '1.5em 0', sm: '5em 0', xs: '5em 0' }
              }}
            />
          </Grid>
        </Grid>
      </Container>
    </RootStyle>
  );
}
